import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { usePwa } from '@/App';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import Barcode from 'react-barcode';
import BarcodeCameraScanner from '@/components/BarcodeCameraScanner';
import { motion, AnimatePresence } from 'framer-motion';
import { Footer } from '@/components/Footer';
import {
    Wallet, CreditCard, Gift, Clock, LogOut, Bell,
    Stamp, Star, Plus, Award, Search, QrCode,
    Compass, ChevronRight, Maximize2,
    Coffee, UtensilsCrossed, Wheat, Scissors, ShoppingBag, Dumbbell,
    HeartPulse, Flower2, Crown, Leaf, Zap, Sparkles, Heart, Gem,
    Music, Wine, Camera, BookOpen, Plane, PawPrint, Palette, Bike, Cake, Pizza,
    Car, Anchor, Flame, Stethoscope, GraduationCap, Trophy, Wrench,
    IceCreamCone, Shirt, Sun, Mountain, Fish, Gamepad2, Headphones,
    MapPin, Umbrella, TreePine, Soup, Sandwich, Beer, Store,
    Percent, Euro, Layers, User, Phone, CalendarDays, Share2, Copy, ExternalLink, Handshake, Tag, Smartphone, X, Upload, PlusSquare, Trash2, Loader2, ScanBarcode, Check, AlertTriangle
} from 'lucide-react';
import { CardPattern } from '@/components/cards/CardPattern';

const themeIconMap = {
    coffee: Coffee, utensils: UtensilsCrossed, wheat: Wheat, scissors: Scissors,
    shopping: ShoppingBag, dumbbell: Dumbbell, health: HeartPulse, flower: Flower2,
    crown: Crown, leaf: Leaf, zap: Zap, sparkles: Sparkles, gift: Gift,
    clock: Clock, music: Music, wine: Wine, camera: Camera, book: BookOpen,
    plane: Plane, paw: PawPrint, palette: Palette, bike: Bike, cake: Cake,
    pizza: Pizza, car: Car, anchor: Anchor, flame: Flame, stethoscope: Stethoscope,
    graduation: GraduationCap, trophy: Trophy, wrench: Wrench, icecream: IceCreamCone,
    shirt: Shirt, sun: Sun, mountain: Mountain, fish: Fish, gamepad: Gamepad2,
    headphones: Headphones, mappin: MapPin, umbrella: Umbrella, tree: TreePine,
    soup: Soup, sandwich: Sandwich, beer: Beer, store: Store,
    star: Star, heart: Heart, gem: Gem, stamp: Stamp,
};

const stampIconMap = themeIconMap;

const cardPalettes = [
    { from: '#059669', to: '#047857', light: '#d1fae5' },
    { from: '#2563eb', to: '#1d4ed8', light: '#dbeafe' },
    { from: '#7c3aed', to: '#6d28d9', light: '#ede9fe' },
    { from: '#dc2626', to: '#b91c1c', light: '#fee2e2' },
    { from: '#d97706', to: '#b45309', light: '#fef3c7' },
    { from: '#0891b2', to: '#0e7490', light: '#cffafe' },
    { from: '#4f46e5', to: '#4338ca', light: '#e0e7ff' },
    { from: '#374151', to: '#1f2937', light: '#f3f4f6' },
];

function getCardColor(index) {
    return cardPalettes[index % cardPalettes.length];
}

function formatTierBenefits(reward) {
    if (!reward || typeof reward === 'string') return reward || '-';
    const parts = [];
    if (reward.discount > 0) parts.push(`-${reward.discount}%`);
    if (reward.free_product) parts.push(`${reward.free_product}/${reward.free_product_freq === 'visit' ? 'visite' : 'mois'}`);
    if (reward.points_multiplier > 1) parts.push(`x${reward.points_multiplier} pts`);
    if (reward.vip_access) parts.push('VIP');
    return parts.length > 0 ? parts.join(' + ') : '-';
}

function getTextColor(card, field) {
    return card[`text_color_${field}`] || card.card_text_color || '#ffffff';
}

function LoyaltyCard({ wallet, index, onTap, onRedeem, onLeave }) {
    const card = wallet.card;
    if (!card) return null;
    const palette = card.card_color_from
        ? { from: card.card_color_from, to: card.card_color_to || card.card_color_from }
        : getCardColor(index);
    const cardType = card.type || 'stamp';
    const progress =
        cardType === 'stamp' ? (wallet.stamps / card.stamp_goal) * 100 :
        cardType === 'points' ? (wallet.points / (card.reward_points_cost || 100)) * 100 :
        cardType === 'cashback' ? ((wallet.cashback_balance || 0) / (card.cashback_min_redeem || 5)) * 100 :
        cardType === 'amount' ? ((wallet.total_spent || 0) / (card.amount_goal || 200)) * 100 :
        cardType === 'tier' ? (() => {
            const paliers = (card.paliers_config || []).sort((a, b) => a.points - b.points);
            const nextP = paliers.find(p => p.points > (wallet.points || 0));
            return nextP ? ((wallet.points || 0) / nextP.points * 100) : 100;
        })() : 0;
    const canRedeem =
        cardType === 'stamp' ? wallet.stamps >= card.stamp_goal :
        cardType === 'points' ? wallet.points >= (card.reward_points_cost || 100) :
        cardType === 'cashback' ? (wallet.cashback_balance || 0) >= (card.cashback_min_redeem || 5) :
        cardType === 'amount' ? wallet.reward_pending : false;
    const pattern = card.card_pattern || 'circles';
    const typeLabel = { stamp: 'TAMPONS', points: 'POINTS', cashback: 'CASHBACK', tier: 'PALIERS', amount: 'MONTANT' }[cardType] || cardType.toUpperCase();

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.08 }}
            className="cursor-pointer group"
            onClick={() => onTap(wallet)}
            data-testid={`loyalty-card-${wallet.id}`}
        >
            <div
                className={`relative overflow-hidden rounded-2xl p-5 shadow-xl transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-2xl ${canRedeem ? 'ring-2 ring-amber-400 ring-offset-2' : ''}`}
                style={{
                    background: card.card_background ? 'none' : `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
                    color: card.card_text_color || '#ffffff',
                }}
            >
                {card.card_background ? (
                    <>
                        <img src={card.card_background} className="absolute inset-0 w-full h-full object-cover" alt="" />
                        <div className="absolute inset-0 bg-black/30" />
                    </>
                ) : (
                    <CardPattern pattern={pattern} />
                )}

                {/* Reward Ready Banner */}
                {canRedeem && (
                    <div className="relative z-10 bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 mb-3 flex items-center gap-2 border border-white/30" data-testid={`reward-ready-${wallet.id}`}>
                        <Gift className="w-4 h-4 text-amber-300 animate-pulse" />
                        <span className="text-xs font-bold">Récompense prête ! Présentez au commerçant</span>
                    </div>
                )}

                {/* Suspended Program Banner — merchant plan expired/inactive OR admin disabled */}
                {(wallet.merchant_active === false || card.admin_disabled === true) && (
                    <div className="relative z-10 bg-amber-400/95 backdrop-blur-sm rounded-lg px-3 py-2 mb-3 flex items-center gap-2 border border-amber-300 text-slate-900" data-testid={`suspended-banner-${wallet.id}`}>
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span className="text-[11px] font-semibold leading-tight">Ce programme est temporairement indisponible</span>
                    </div>
                )}

                {/* Header */}
                <div className="flex items-start justify-between mb-5 relative z-10">
                    <div className="flex items-center gap-3">
                        {card.card_logo ? (
                            <img src={card.card_logo} className="w-10 h-10 rounded-lg object-cover border border-white/20 shrink-0" alt="" />
                        ) : (
                            <div className="w-10 h-10 bg-white/15 rounded-lg flex items-center justify-center shrink-0">
                                {(() => { const I = themeIconMap[card.card_icon] || Gift; return <I className="w-5 h-5 opacity-70" />; })()}
                            </div>
                        )}
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] mb-0.5" style={{ color: getTextColor(card, 'label'), opacity: 0.7 }}>Carte Fidelite</p>
                            <h3 className="font-heading text-lg leading-tight" style={{ color: getTextColor(card, 'title'), fontWeight: card.card_text_bold === false ? 400 : 700 }}>{card.name}</h3>
                            <p className="text-xs mt-0.5" style={{ color: getTextColor(card, 'subtitle'), opacity: 0.8 }}>{card.merchant_name}</p>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <Badge className="bg-white/20 text-white border-0 text-[10px] font-semibold px-2 py-0.5">
                            {typeLabel}
                        </Badge>
                        <Maximize2 className="w-3.5 h-3.5 text-white/40 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                </div>

                {/* Content row: QR + Progress */}
                <div className="flex gap-4 relative z-10">
                    {/* QR Code */}
                    <div className="bg-white rounded-xl p-2 shrink-0 shadow-lg">
                        <QRCode value={wallet.qr_code || 'N/A'} size={76} fgColor={palette.from} />
                    </div>

                    {/* Progress */}
                    <div className="flex-1 flex flex-col justify-center min-w-0">
                        {cardType === 'stamp' ? (
                            <>
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                    {Array.from({ length: card.stamp_goal }).map((_, i) => {
                                        const SIcon = stampIconMap[card.card_stamp_style] || Stamp;
                                        return (
                                            <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${i < wallet.stamps ? 'bg-white shadow-sm' : 'bg-white/15 border border-white/25'}`}>
                                                {i < wallet.stamps && <SIcon className="w-3 h-3" style={{ color: palette.from }} />}
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-sm font-medium text-white/90">{wallet.stamps}/{card.stamp_goal} tampons</p>
                            </>
                        ) : cardType === 'points' ? (
                            <>
                                <div className="bg-white/20 rounded-full h-2.5 mb-2 overflow-hidden">
                                    <motion.div className="bg-white rounded-full h-2.5" initial={{ width: 0 }} animate={{ width: `${Math.min(progress, 100)}%` }} transition={{ duration: 0.8, delay: 0.3 }} />
                                </div>
                                <p className="text-sm font-medium text-white/90">{wallet.points}/{card.reward_points_cost} points</p>
                            </>
                        ) : cardType === 'cashback' ? (
                            <>
                                <p className="text-xl font-bold text-white mb-1">{(wallet.cashback_balance || 0).toFixed(2)} €</p>
                                <div className="bg-white/20 rounded-full h-2 mb-1 overflow-hidden">
                                    <motion.div className="bg-white rounded-full h-2" initial={{ width: 0 }} animate={{ width: `${Math.min(progress, 100)}%` }} transition={{ duration: 0.8, delay: 0.3 }} />
                                </div>
                                <p className="text-[10px]" style={{ color: getTextColor(card, 'progress'), opacity: 0.7 }}>{card.cashback_percent}% cashback — Min. {card.cashback_min_redeem}€</p>
                            </>
                        ) : cardType === 'tier' ? (
                            <>
                                {(() => {
                                    const pts = wallet.points || 0;
                                    const paliers = (card.paliers_config || []).sort((a, b) => a.points - b.points);
                                    const availableCount = paliers.filter(p => p.points <= pts).length;
                                    return (
                                        <>
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-lg font-bold text-white">{pts} pts</span>
                                                {availableCount > 0 && (
                                                    <span className="text-[10px] bg-white/25 text-white px-2 py-0.5 rounded-full font-bold">{availableCount} remise{availableCount > 1 ? 's' : ''}</span>
                                                )}
                                            </div>
                                            {/* Mini paliers timeline */}
                                            <div className="space-y-1">
                                                {paliers.slice(0, 4).map((p, i) => {
                                                    const reached = pts >= p.points;
                                                    const isCurrent = !reached && (i === 0 || pts >= paliers[i - 1].points);
                                                    return (
                                                        <div key={i} className="flex items-center gap-2">
                                                            <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 ${reached ? 'bg-white' : isCurrent ? 'border-2 border-white/80 bg-white/20' : 'border border-white/30 bg-white/10'}`}>
                                                                {reached && <Check className="w-2 h-2" style={{ color: palette.from }} />}
                                                            </div>
                                                            <div className="flex-1 flex items-center justify-between min-w-0">
                                                                <span className={`text-[10px] truncate ${reached ? 'text-white font-semibold' : 'text-white/50'}`}>{p.reward}</span>
                                                                <span className={`text-[9px] shrink-0 ml-1 ${reached ? 'text-white/80' : 'text-white/40'}`}>{p.points} pts</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {paliers.length > 4 && (
                                                    <p className="text-[9px] text-white/40 text-center">+{paliers.length - 4} autre{paliers.length - 4 > 1 ? 's' : ''} palier{paliers.length - 4 > 1 ? 's' : ''}</p>
                                                )}
                                            </div>
                                        </>
                                    );
                                })()}
                            </>
                        ) : cardType === 'amount' ? (
                            <>
                                <div className="bg-white/20 rounded-full h-2.5 mb-2 overflow-hidden">
                                    <motion.div className="bg-white rounded-full h-2.5" initial={{ width: 0 }} animate={{ width: `${Math.min(progress, 100)}%` }} transition={{ duration: 0.8, delay: 0.3 }} />
                                </div>
                                <p className="text-sm font-medium text-white/90">{(wallet.total_spent || 0).toFixed(2)}€ / {card.amount_goal}€</p>
                            </>
                        ) : null}
                        <div className="flex items-center gap-1 mt-1.5">
                            <Gift className="w-3 h-3" style={{ color: getTextColor(card, 'reward'), opacity: 0.6 }} />
                            <p className="text-xs truncate" style={{ color: getTextColor(card, 'reward'), opacity: 0.7 }}>{card.reward_description}</p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10 relative z-10">
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono tracking-wider" style={{ color: getTextColor(card, 'footer'), opacity: 0.5 }}>{wallet.qr_code}</span>
                        {wallet.rewards_claimed > 0 && (
                            <span className="text-[10px]" style={{ color: getTextColor(card, 'footer'), opacity: 0.6 }}>
                                <Award className="w-3 h-3 inline mr-0.5" />{wallet.rewards_claimed} offerte{wallet.rewards_claimed > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {canRedeem && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onRedeem(wallet); }}
                                className="bg-white/20 hover:bg-white/30 text-[10px] font-bold px-3 py-1 rounded-full transition-all active:scale-95"
                                style={{ color: getTextColor(card, 'footer') }}
                                data-testid={`redeem-card-${wallet.id}`}
                            >
                                Reclamer
                            </button>
                        )}
                        <button
                            onClick={(e) => { e.stopPropagation(); onLeave(wallet); }}
                            className="bg-white/10 hover:bg-red-500/30 text-[10px] px-2 py-1 rounded-full transition-all active:scale-95"
                            style={{ color: getTextColor(card, 'footer'), opacity: 0.5 }}
                            data-testid={`leave-card-${wallet.id}`}
                            title="Se désabonner"
                        >
                            <Trash2 className="w-3 h-3" />
                        </button>
                        <span className="text-[10px] font-semibold" style={{ color: getTextColor(card, 'footer'), opacity: 0.4 }}>FidelRun</span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

export default function CustomerDashboard() {
    const { user, logout } = useAuth();
    const pwa = usePwa();
    const [wallets, setWallets] = useState([]);
    const [walletsLoading, setWalletsLoading] = useState(true);
    const [transactions, setTransactions] = useState([]);
    const [stats, setStats] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [discoverCards, setDiscoverCards] = useState([]);
    const [partnerCards, setPartnerCards] = useState([]);
    const [selectedWallet, setSelectedWallet] = useState(null);
    const [showNotifPanel, setShowNotifPanel] = useState(false);
    const [walletSearch, setWalletSearch] = useState('');
    const [discoverSearch, setDiscoverSearch] = useState('');
    const [showProfile, setShowProfile] = useState(false);
    const [profileForm, setProfileForm] = useState({ birthday: '', phone: '', rgpd_consent: false });
    const [clientPromos, setClientPromos] = useState([]);
    const [leaveWallet, setLeaveWallet] = useState(null);
    const [externalCards, setExternalCards] = useState([]);
    const [showAddExternal, setShowAddExternal] = useState(false);
    const [externalForm, setExternalForm] = useState({ shop_name: '', card_number: '' });
    const [externalLoading, setExternalLoading] = useState(false);
    const [previewExternal, setPreviewExternal] = useState(null);
    const [barcodeZoom, setBarcodeZoom] = useState(false);
    const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
    const [walletSubTab, setWalletSubTab] = useState('fidelrun');

    const fetchData = useCallback(async () => {
        try {
            const [walletsRes, txRes, statsRes, notifRes, meRes, promosRes, extCardsRes] = await Promise.all([
                api.get('/wallet'),
                api.get('/transactions'),
                api.get('/stats/customer'),
                api.get('/notifications'),
                api.get('/auth/me'),
                api.get('/promos'),
                api.get('/external-cards'),
            ]);
            setWallets(walletsRes.data);
            setTransactions(txRes.data);
            setStats(statsRes.data);
            setNotifications(notifRes.data);
            setProfileForm({ birthday: meRes.data.birthday || '', phone: meRes.data.phone || '', rgpd_consent: meRes.data.rgpd_consent || false });
            setClientPromos(promosRes.data);
            setExternalCards(extCardsRes.data);
            setWalletsLoading(false);
            // Tag OneSignal with merchant IDs + user ID for push targeting
            try {
                const OS = window.OneSignal;
                if (OS && OS.User) {
                    const tags = {};
                    walletsRes.data.forEach(w => { if (w.merchant_id) tags[`merchant_${w.merchant_id}`] = 'true'; });
                    if (user?.id) tags[`user_${user.id}`] = 'true';
                    if (Object.keys(tags).length > 0) await OS.User.addTags(tags);
                }
            } catch (e) { /* onesignal not ready */ }
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Auto-join card if redirected from /rejoindre/:cardId
    useEffect(() => {
        const pendingCard = localStorage.getItem('fidelrun_join_card');
        if (pendingCard) {
            localStorage.removeItem('fidelrun_join_card');
            api.post(`/wallet/join/${pendingCard}`)
                .then(() => { toast.success('Vous avez rejoint le programme !'); fetchData(); })
                .catch(err => {
                    const msg = err.response?.data?.detail || '';
                    if (msg === 'Deja membre') toast.info('Vous êtes déjà membre de ce programme.');
                    else toast.error(msg || 'Erreur lors de l\'inscription au programme.');
                });
        }
    }, [fetchData]);

    // Auto-refresh when tab regains focus (e.g. after merchant validates a reward)
    useEffect(() => {
        const onVisibility = () => { if (document.visibilityState === 'visible') fetchData(); };
        document.addEventListener('visibilitychange', onVisibility);
        // Also poll every 30s so open tabs stay in sync
        const interval = setInterval(fetchData, 30000);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            clearInterval(interval);
        };
    }, [fetchData]);

    const fetchDiscover = useCallback(async () => {
        try {
            const [discRes, partRes] = await Promise.all([
                api.get('/cards/discover'),
                api.get('/partners/cards'),
            ]);
            const partnerIds = new Set(partRes.data.map(c => c.id));
            setDiscoverCards(discRes.data.filter(c => !partnerIds.has(c.id)));
            setPartnerCards(partRes.data);
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => { fetchDiscover(); }, [fetchDiscover]);

    const handleJoin = async (cardId) => {
        try {
            const res = await api.post(`/wallet/join/${cardId}`);
            toast.success('Carte ajoutée à votre wallet !');
            try {
                const OS = window.OneSignal;
                if (OS && OS.User) OS.User.addTag(`merchant_${res.data.merchant_id}`, 'true');
            } catch (e) { /* onesignal not ready */ }
            fetchData();
            fetchDiscover();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleRedeem = async (wallet) => {
        try {
            const cardType = wallet.card?.type;
            const txType = cardType === 'cashback' ? 'use_cashback' : 'redeem';
            await api.post('/transactions', {
                wallet_id: wallet.id,
                type: txType,
                amount: cardType === 'cashback' ? (wallet.cashback_balance || 0) : 1,
                description: 'Récompense réclamée'
            });
            toast.success('Récompense réclamée avec succès !');
            await fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleLeaveCard = async () => {
        if (!leaveWallet) return;
        try {
            await api.delete(`/wallet/${leaveWallet.id}`);
            toast.success('Vous vous êtes désabonné de cette carte.');
            setLeaveWallet(null);
            setSelectedWallet(null);
            await fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors du désabonnement');
        }
    };

    const toDeepLink = (url, platform) => {
        if (!url) return url;
        try {
            const u = new URL(url.startsWith('http') ? url : `https://${url}`);
            const path = u.pathname.replace(/^\//, '');
            if (platform === 'instagram') return `instagram://user?username=${path}`;
            if (platform === 'facebook') return `fb://page/${path}`;
            if (platform === 'tiktok') return `snssdk1233://user/profile/${path.replace('@', '')}`;
            if (platform === 'snapchat') return `snapchat://add/${path.replace('add/', '')}`;
        } catch { /* ignore */ }
        return url;
    };

    const claimSocialBonus = async (walletId, platform, link) => {
        window.open(toDeepLink(link, platform), '_blank');
        try {
            const res = await api.post(`/boost/social/${walletId}/${platform}`);
            toast.success(`+${res.data.card?.social_bonus || 0} bonus ${platform} !`);
            await fetchData();
        } catch (err) {
            if (err.response?.status !== 400) toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const claimReviewBonus = async (walletId, platform, link) => {
        window.open(link.startsWith('http') ? link : `https://${link}`, '_blank');
        try {
            const res = await api.post(`/boost/review/${walletId}/${platform}`);
            toast.success(`+${res.data.card?.review_bonus || 0} bonus avis ${platform} !`);
            await fetchData();
        } catch (err) {
            if (err.response?.status !== 400) toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const copyReferralCode = (code) => {
        navigator.clipboard.writeText(code);
        toast.success('Code parrain copie !');
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    const markNotifRead = async (notifId) => {
        try {
            await api.put(`/notifications/${notifId}/read`);
            setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
        } catch (err) { console.error(err); }
    };

    const markAllRead = async () => {
        try {
            await api.put('/notifications/read-all');
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setShowNotifPanel(false);
        } catch (err) { console.error(err); }
    };

    const installApp = async () => {
        if (pwa?.installPrompt) {
            await pwa.installPrompt.prompt();
            const result = await pwa.installPrompt.userChoice;
            if (result.outcome === 'accepted') toast.success('FidelRun installe !');
            pwa.setInstallPrompt(null);
        }
    };

    const [pushPermission, setPushPermission] = useState('default');
    const [showIosBanner, setShowIosBanner] = useState(false);
    const [showAndroidHints, setShowAndroidHints] = useState(false);

    // Detect iOS Safari not in standalone mode
    useEffect(() => {
        const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
        const dismissed = localStorage.getItem('fidelrun_ios_banner_dismissed');
        if (isIos && !isStandalone && !dismissed) {
            setShowIosBanner(true);
        }
        // Android/other: show icon hints if not installed and not dismissed
        if (!isIos && !isStandalone && !localStorage.getItem('fidelrun_android_hints_dismissed')) {
            setShowAndroidHints(true);
        }
    }, []);

    useEffect(() => {
        // Check push subscription status
        const checkPush = () => {
            try {
                if (window.__onesignal_error) {
                    setPushPermission('error');
                    return;
                }
                // Check native browser permission first
                if ('Notification' in window && Notification.permission === 'denied') {
                    setPushPermission('denied');
                    return;
                }
                if ('Notification' in window && Notification.permission === 'granted') {
                    const OS = window.OneSignal;
                    if (OS && OS.User && OS.User.PushSubscription && OS.User.PushSubscription.optedIn) {
                        setPushPermission('granted');
                        return;
                    }
                    // Permission granted but not opted in - auto re-subscribe will handle it
                }
                const OS = window.OneSignal;
                if (OS && OS.User && OS.User.PushSubscription && OS.User.PushSubscription.optedIn) {
                    setPushPermission('granted');
                }
            } catch(e) {}
        };
        checkPush();
        const timer = setTimeout(checkPush, 3000);
        const timer2 = setTimeout(checkPush, 8000);
        return () => { clearTimeout(timer); clearTimeout(timer2); };
    }, []);

    const requestPushPermission = async () => {
        try {
            // Step 1: IMMEDIATELY request native browser permission (preserves user gesture)
            let nativePermission = 'default';
            if ('Notification' in window) {
                nativePermission = Notification.permission;
                if (nativePermission === 'default') {
                    nativePermission = await Notification.requestPermission();
                }
            }

            if (nativePermission === 'denied') {
                setPushPermission('denied');
                toast.error(
                    'Notifications bloquées. Allez dans les paramètres de votre navigateur > Notifications > Autoriser pour ce site, puis rechargez la page.',
                    { duration: 10000 }
                );
                return;
            }

            // Step 2: Ensure service worker is registered (critical for push on mobile)
            let swReg = null;
            if ('serviceWorker' in navigator) {
                try {
                    swReg = await navigator.serviceWorker.getRegistration('/');
                    if (!swReg) {
                        console.log('[Push] No service worker found, registering manually...');
                        swReg = await navigator.serviceWorker.register('/OneSignalSDKWorker.js', { scope: '/' });
                        await navigator.serviceWorker.ready;
                        console.log('[Push] Service worker registered manually');
                    } else {
                        console.log('[Push] Service worker already registered:', swReg.scope);
                    }
                } catch (swErr) {
                    console.error('[Push] Service worker registration error:', swErr);
                }
            }

            // Step 3: Wait for OneSignal to be ready (up to 15 seconds)
            let OS = window.OneSignal;
            if (!OS || !OS.Notifications || !window.__onesignal_ready) {
                toast.info('Initialisation du service de notifications...', { duration: 3000 });
                for (let i = 0; i < 30; i++) {
                    await new Promise(r => setTimeout(r, 500));
                    OS = window.OneSignal;
                    if (OS && OS.Notifications && window.__onesignal_ready) break;
                    if (window.__onesignal_error) {
                        console.error('[Push] OneSignal init failed:', window.__onesignal_error);
                        break;
                    }
                }
            }

            // Step 4: Try OneSignal optIn if SDK is ready
            let success = false;
            if (window.__onesignal_ready && OS && OS.User && OS.User.PushSubscription) {
                try {
                    await OS.User.PushSubscription.optIn();
                    await new Promise(r => setTimeout(r, 2000));
                    if (OS.User.PushSubscription.optedIn) {
                        success = true;
                        console.log('[Push] OneSignal optIn succeeded');
                    }
                } catch (optErr) {
                    console.error('[Push] OneSignal optIn error:', optErr);
                }

                // Retry: force re-subscribe
                if (!success) {
                    try {
                        console.log('[Push] Trying force re-subscribe...');
                        await OS.User.PushSubscription.optOut();
                        await new Promise(r => setTimeout(r, 1000));
                        await OS.User.PushSubscription.optIn();
                        await new Promise(r => setTimeout(r, 2000));
                        if (OS.User.PushSubscription.optedIn) {
                            success = true;
                            console.log('[Push] Force re-subscribe succeeded');
                        }
                    } catch (retryErr) {
                        console.error('[Push] Force re-subscribe error:', retryErr);
                    }
                }
            }

            // Step 5: FALLBACK - Manual push subscription if OneSignal failed
            if (!success && swReg && nativePermission === 'granted') {
                console.log('[Push] OneSignal optIn failed - trying manual push subscription...');
                try {
                    // Get VAPID key from OneSignal config
                    const syncResp = await fetch('https://api.onesignal.com/sync/716aa58f-2072-4a62-b2af-99bd34dc237e/web');
                    const syncData = await syncResp.json();
                    const vapidKey = syncData?.config?.vapid_public_key;

                    if (vapidKey) {
                        // Convert VAPID key to Uint8Array
                        const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
                        const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
                        const rawData = window.atob(base64);
                        const outputArray = new Uint8Array(rawData.length);
                        for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);

                        // Create push subscription directly via Push API
                        const existingSub = await swReg.pushManager.getSubscription();
                        if (existingSub) {
                            await existingSub.unsubscribe();
                        }

                        const subscription = await swReg.pushManager.subscribe({
                            userVisibleOnly: true,
                            applicationServerKey: outputArray,
                        });

                        console.log('[Push] Manual subscription created:', subscription.endpoint?.substring(0, 50));

                        // Register this subscription with OneSignal via their player API
                        const subJson = subscription.toJSON();
                        const playerData = {
                            app_id: '716aa58f-2072-4a62-b2af-99bd34dc237e',
                            device_type: 5,
                            identifier: subscription.endpoint,
                            web_auth: subJson.keys?.auth || '',
                            web_p256: subJson.keys?.p256dh || '',
                            tags: {},
                        };
                        // Add merchant tags
                        wallets.slice(0, 3).forEach(w => {
                            if (w.merchant_id) playerData.tags[`merchant_${w.merchant_id}`] = 'true';
                        });

                        const createResp = await fetch('https://api.onesignal.com/players', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(playerData),
                        });
                        const createResult = await createResp.json();
                        console.log('[Push] OneSignal player created:', createResult);

                        if (createResult.success || createResult.id) {
                            success = true;

                            // If OneSignal SDK is available, try to link this subscription
                            if (OS && window.__onesignal_ready) {
                                try {
                                    await OS.login(user.id);
                                    const merchantTags = {};
                                    wallets.slice(0, 3).forEach(w => {
                                        if (w.merchant_id) merchantTags[`merchant_${w.merchant_id}`] = 'true';
                                    });
                                    await OS.User.addTags(merchantTags);
                                } catch (tagErr) {
                                    console.log('[Push] Tag error after manual sub:', tagErr);
                                }
                            }
                        }
                    }
                } catch (manualErr) {
                    console.error('[Push] Manual subscription error:', manualErr);
                }
            }

            if (success) {
                setPushPermission('granted');
                toast.success('Notifications push activées avec succès !');
                // Set tags if not already done
                if (OS && window.__onesignal_ready) {
                    try {
                        await OS.login(user.id);
                        const merchantTags = {};
                        wallets.slice(0, 3).forEach(w => {
                            if (w.merchant_id) merchantTags[`merchant_${w.merchant_id}`] = 'true';
                        });
                        await OS.User.addTags(merchantTags);
                    } catch (tagErr) { console.log('[Push] Tag error:', tagErr); }
                }
            } else if (nativePermission === 'granted') {
                setPushPermission('default');
                toast.info(
                    'Permission accordée ! Les notifications peuvent prendre un moment à s\'activer. Rechargez la page dans quelques secondes.',
                    { duration: 8000 }
                );
            } else {
                toast.error('Impossible d\'activer les notifications. Rechargez la page et réessayez.', { duration: 8000 });
            }
        } catch (e) {
            console.error('[Push] Error:', e);
            toast.error(`Erreur lors de l'activation: ${e.message || 'Rechargez la page et réessayez.'}`, { duration: 8000 });
        }
    };

    const saveProfile = async () => {
        try {
            await api.put('/auth/profile', profileForm);
            toast.success('Profil mis a jour !');
            setShowProfile(false);
        } catch (err) {
            toast.error('Erreur lors de la mise a jour');
        }
    };

    const handleAddExternalCard = async () => {
        if (!externalForm.shop_name.trim()) { toast.error('Entrez le nom du commerce'); return; }
        if (!externalForm.card_number.trim()) { toast.error('Entrez un numéro de carte'); return; }
        setExternalLoading(true);
        try {
            await api.post('/external-cards', {
                shop_name: externalForm.shop_name,
                card_number: externalForm.card_number,
            });
            toast.success('Carte ajoutée !');
            setShowAddExternal(false);
            setExternalForm({ shop_name: '', card_number: '' });
            const res = await api.get('/external-cards');
            setExternalCards(res.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setExternalLoading(false);
        }
    };

    const handleDeleteExternalCard = async (cardId) => {
        try {
            await api.delete(`/external-cards/${cardId}`);
            toast.success('Carte supprimée');
            setExternalCards(prev => prev.filter(c => c.id !== cardId));
            setPreviewExternal(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const txTypeLabels = {
        add_stamp: 'Tampon ajouté',
        add_points: 'Points ajoutés',
        redeem: 'Récompense réclamée',
        remove_points: 'Points retirés',
    };

    return (
        <div className="min-h-screen bg-gray-50/80">
            {/* Header */}
            <header className="glass-header sticky top-0 z-50" data-testid="customer-header">
                <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-12 h-12 rounded-lg overflow-hidden">
                            <img src="/logo-fr-small.png" alt="FidelRun" className="w-12 h-12 object-contain" />
                        </div>
                        <span className="font-heading text-lg font-bold text-emerald-950">FidelRun</span>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-3">
                        <div className="relative">
                            <button
                                onClick={() => setShowNotifPanel(!showNotifPanel)}
                                className="relative p-1.5 rounded-lg hover:bg-emerald-50 transition-colors"
                                data-testid="notif-bell-btn"
                            >
                                <Bell className="w-5 h-5 text-slate-500" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-emerald-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">{unreadCount}</span>
                                )}
                            </button>

                            {/* Notification Dropdown */}
                            {showNotifPanel && (
                                <>
                                    <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setShowNotifPanel(false)} />
                                    <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-14 sm:top-full sm:mt-2 sm:w-80 bg-white rounded-xl shadow-xl border border-emerald-100 z-50 overflow-hidden" data-testid="notif-dropdown">
                                        <div className="p-3 border-b border-emerald-50 flex items-center justify-between">
                                            <span className="text-sm font-bold text-slate-800">Notifications</span>
                                            {unreadCount > 0 && (
                                                <button onClick={markAllRead} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium" data-testid="mark-all-read-btn">
                                                    Tout marquer comme lu
                                                </button>
                                            )}
                                        </div>
                                        <div className="max-h-72 overflow-y-auto">
                                            {notifications.length === 0 ? (
                                                <p className="p-4 text-sm text-slate-400 text-center">Aucune notification</p>
                                            ) : (
                                                notifications.slice(0, 10).map(n => (
                                                    <div
                                                        key={n.id}
                                                        className={`p-3 border-b border-slate-50 flex items-start gap-2.5 transition-colors ${n.read ? 'bg-white opacity-60' : 'bg-emerald-50/40'}`}
                                                        data-testid={`notif-item-${n.id}`}
                                                    >
                                                        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${n.read ? 'bg-slate-200' : 'bg-emerald-500'}`} />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-semibold text-slate-700">{n.title}</p>
                                                            <p className="text-[11px] text-slate-500 leading-snug break-words">{n.message}</p>
                                                        </div>
                                                        {!n.read && (
                                                            <button onClick={() => markNotifRead(n.id)} className="text-[10px] text-emerald-500 hover:text-emerald-700 font-medium shrink-0 mt-0.5" data-testid={`dismiss-notif-${n.id}`}>
                                                                Lu
                                                            </button>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        {pwa?.installPrompt && (
                            <button
                                onClick={installApp}
                                className="flex items-center gap-1 px-2 py-1.5 bg-emerald-500 text-white rounded-lg text-[11px] sm:text-xs font-bold hover:bg-emerald-600 transition-colors animate-pulse"
                                data-testid="install-pwa-btn"
                            >
                                <Smartphone className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Installer</span>
                            </button>
                        )}
                        {pushPermission !== 'granted' && 'Notification' in window && (
                            <button
                                onClick={requestPushPermission}
                                className={`flex items-center gap-1 px-2 py-1.5 text-white rounded-lg text-[11px] sm:text-xs font-bold transition-colors ${pushPermission === 'denied' || pushPermission === 'error' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-500 hover:bg-blue-600'}`}
                                data-testid="enable-push-btn"
                            >
                                <Bell className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{pushPermission === 'denied' ? 'Notifs bloquées' : pushPermission === 'error' ? 'Notifs indisponibles' : 'Activer les notifs'}</span>
                            </button>
                        )}
                        <button onClick={() => setShowProfile(true)} className="p-1.5 rounded-lg hover:bg-emerald-50 transition-colors" data-testid="profile-btn">
                            <User className="w-4 h-4 text-slate-500" />
                            {(!profileForm.birthday || !profileForm.phone) && <span className="w-2 h-2 rounded-full bg-orange-400 absolute -top-0.5 -right-0.5" />}
                        </button>
                        <button onClick={logout} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors" data-testid="logout-btn">
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </header>

            {/* iOS PWA Installation Guide */}
            <AnimatePresence>
                {showIosBanner && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mx-4 sm:mx-6 mt-3 max-w-screen-xl lg:mx-auto"
                        data-testid="ios-pwa-banner"
                    >
                        <div className="relative bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-4 text-white shadow-lg overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                            <button
                                onClick={() => { setShowIosBanner(false); localStorage.setItem('fidelrun_ios_banner_dismissed', '1'); }}
                                className="absolute top-2.5 right-2.5 p-1 rounded-full hover:bg-white/10 transition-colors"
                                data-testid="ios-banner-close"
                            >
                                <X className="w-4 h-4 text-slate-400" />
                            </button>
                            <p className="text-sm font-bold text-emerald-400 mb-3">Installez FidelRun sur votre iPhone</p>
                            <div className="flex items-start gap-4">
                                <div className="flex flex-col items-center gap-1 shrink-0">
                                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                                        <Upload className="w-5 h-5 text-blue-400" />
                                    </div>
                                    <span className="text-[10px] text-slate-400 font-medium text-center leading-tight">Appuyez sur<br/>Partager</span>
                                </div>
                                <ChevronRight className="w-4 h-4 text-slate-600 mt-3 shrink-0" />
                                <div className="flex flex-col items-center gap-1 shrink-0">
                                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                                        <PlusSquare className="w-5 h-5 text-emerald-400" />
                                    </div>
                                    <span className="text-[10px] text-slate-400 font-medium text-center leading-tight">Sur l'écran<br/>d'accueil</span>
                                </div>
                                <ChevronRight className="w-4 h-4 text-slate-600 mt-3 shrink-0" />
                                <div className="flex flex-col items-center gap-1 shrink-0">
                                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                                        <Sparkles className="w-5 h-5 text-amber-400" />
                                    </div>
                                    <span className="text-[10px] text-slate-400 font-medium text-center leading-tight">Profitez<br/>de l'app !</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Android Icon Hints Banner */}
            <AnimatePresence>
                {showAndroidHints && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mx-4 sm:mx-6 mt-3 max-w-screen-xl lg:mx-auto sm:hidden"
                        data-testid="android-hints-banner"
                    >
                        <div className="relative bg-white rounded-xl p-3 shadow-sm border border-slate-100">
                            <button
                                onClick={() => { setShowAndroidHints(false); localStorage.setItem('fidelrun_android_hints_dismissed', '1'); }}
                                className="absolute top-2 right-2 p-0.5 rounded-full hover:bg-slate-100 transition-colors"
                                data-testid="android-hints-close"
                            >
                                <X className="w-3.5 h-3.5 text-slate-400" />
                            </button>
                            <div className="flex items-center gap-3 pr-5">
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center">
                                        <Smartphone className="w-3.5 h-3.5 text-white" />
                                    </div>
                                    <span className="text-[11px] text-slate-600 leading-tight">Installer<br/>l'app</span>
                                </div>
                                <div className="w-px h-8 bg-slate-200 shrink-0" />
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center">
                                        <Bell className="w-3.5 h-3.5 text-white" />
                                    </div>
                                    <span className="text-[11px] text-slate-600 leading-tight">Activer les<br/>notifications</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <main className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6">
                <Tabs defaultValue="wallet" className="space-y-6" onValueChange={(v) => { if (v === 'discover') fetchDiscover(); }}>
                    <div className="tabs-wrapper">
                    <TabsList className="bg-white border border-emerald-100/50 rounded-xl p-1 w-full justify-start mobile-tabs scrollbar-hide" data-testid="customer-tabs">
                        <TabsTrigger value="wallet" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg gap-1.5 text-sm shrink-0" data-testid="tab-wallet">
                            <Wallet className="w-4 h-4" /> Mon Wallet
                        </TabsTrigger>
                        <TabsTrigger value="discover" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg gap-1.5 text-sm shrink-0" data-testid="tab-discover">
                            <Compass className="w-4 h-4" /> Découvrir
                        </TabsTrigger>
                        <TabsTrigger value="history" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg gap-1.5 text-sm shrink-0" data-testid="tab-history">
                            <Clock className="w-4 h-4" /> Historique
                        </TabsTrigger>
                        <TabsTrigger value="promos" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg gap-1.5 text-sm shrink-0" data-testid="tab-promos">
                            <Tag className="w-4 h-4" /> Promos
                            {clientPromos.length > 0 && (
                                <Badge className="bg-red-500 text-white border-0 text-[9px] px-1.5 py-0 ml-1">{clientPromos.length}</Badge>
                            )}
                        </TabsTrigger>
                    </TabsList>
                    </div>

                    {/* WALLET TAB */}
                    <TabsContent value="wallet" className="space-y-5">
                        {/* Stats */}
                        {stats && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="customer-stats">
                                {[
                                    { label: 'Cartes', value: stats.total_cards, icon: CreditCard },
                                    { label: 'Points', value: stats.total_points, icon: Star },
                                    { label: 'Tampons', value: stats.total_stamps, icon: Stamp },
                                    { label: 'Récompenses', value: stats.total_rewards, icon: Award },
                                ].map((s, i) => (
                                    <Card key={i} className="border-emerald-100/50 rounded-xl">
                                        <CardContent className="p-3 flex items-center gap-3">
                                            <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0">
                                                <s.icon className="w-4 h-4 text-emerald-600" />
                                            </div>
                                            <div>
                                                <p className="font-heading text-xl font-bold leading-none">{s.value}</p>
                                                <p className="text-[11px] text-slate-500">{s.label}</p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}

                        {/* Sub-tabs: FidelRun / Autres cartes */}
                        <div className="flex items-center justify-between" data-testid="wallet-subtabs">
                            <div className="flex items-center bg-slate-100 rounded-full p-1 gap-1 flex-1 sm:flex-initial">
                                <button
                                    onClick={() => setWalletSubTab('fidelrun')}
                                    className={`flex-1 sm:flex-initial px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${walletSubTab === 'fidelrun' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                    data-testid="subtab-fidelrun"
                                >
                                    Cartes FidelRun
                                </button>
                                <button
                                    onClick={() => setWalletSubTab('external')}
                                    className={`flex-1 sm:flex-initial px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${walletSubTab === 'external' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                    data-testid="subtab-external"
                                >
                                    Autres cartes
                                    {externalCards.length > 0 && (
                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${walletSubTab === 'external' ? 'bg-slate-800 text-white' : 'bg-slate-300 text-slate-600'}`}>{externalCards.length}</span>
                                    )}
                                </button>
                            </div>
                            {walletSubTab === 'external' && (
                                <Button
                                    size="sm"
                                    onClick={() => setShowAddExternal(true)}
                                    className="bg-slate-800 hover:bg-slate-700 text-white gap-1 text-xs h-8 px-3 ml-2 shrink-0"
                                    data-testid="add-external-card-btn"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                </Button>
                            )}
                        </div>

                        {/* FidelRun Cards Sub-tab */}
                        {walletSubTab === 'fidelrun' && (
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs text-slate-400">Appuyez pour agrandir le QR</p>
                                </div>

                                {wallets.length > 1 && (
                                    <div className="relative mb-4">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Rechercher une carte ou un commerce..."
                                            value={walletSearch}
                                            onChange={e => setWalletSearch(e.target.value)}
                                            className="w-full pl-9 pr-4 py-2.5 bg-white border border-emerald-100 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
                                            data-testid="wallet-search-input"
                                        />
                                        {walletSearch && (
                                            <button onClick={() => setWalletSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="wallet-search-clear">
                                                <span className="text-xs font-medium">✕</span>
                                            </button>
                                        )}
                                    </div>
                                )}

                                {(() => {
                                    const q = walletSearch.toLowerCase().trim();
                                    const filtered = q ? wallets.filter(w => {
                                        const card = w.card || {};
                                        return (card.name || '').toLowerCase().includes(q) || (card.merchant_name || '').toLowerCase().includes(q);
                                    }) : wallets;
                                    return filtered.length === 0 && wallets.length > 0 ? (
                                        <Card className="border-emerald-100/50 rounded-2xl">
                                            <CardContent className="p-8 text-center">
                                                <Search className="w-10 h-10 text-emerald-200 mx-auto mb-3" />
                                                <p className="text-sm text-slate-500">Aucune carte trouvée pour "{walletSearch}"</p>
                                            </CardContent>
                                        </Card>
                                    ) : filtered.length === 0 ? (
                                    walletsLoading ? (
                                        <div className="flex items-center justify-center py-16">
                                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" />
                                        </div>
                                    ) : (
                                    <Card className="border-emerald-100/50 rounded-2xl border-dashed">
                                        <CardContent className="p-10 text-center">
                                            <Wallet className="w-14 h-14 text-emerald-200 mx-auto mb-4" />
                                            <p className="text-slate-500 mb-1 font-medium">Votre wallet est vide</p>
                                            <p className="text-xs text-slate-400 mb-4">Découvrez des programmes de fidélité près de chez vous</p>
                                            <Button
                                                onClick={() => { fetchDiscover(); document.querySelector('[data-testid="tab-discover"]')?.click(); }}
                                                className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5"
                                                data-testid="discover-empty-btn"
                                            >
                                                <Compass className="w-4 h-4" /> Découvrir des programmes
                                            </Button>
                                        </CardContent>
                                    </Card>
                                    )
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {filtered.map((w, i) => (
                                                <div key={w.id} className="space-y-3">
                                                    <LoyaltyCard
                                                        wallet={w}
                                                        index={i}
                                                        onTap={setSelectedWallet}
                                                        onRedeem={handleRedeem}
                                                        onLeave={setLeaveWallet}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {/* External Cards Sub-tab */}
                        {walletSubTab === 'external' && (
                            <div data-testid="external-cards-section">
                                {externalCards.length === 0 ? (
                                    <Card className="border-slate-200/50 rounded-2xl border-dashed">
                                        <CardContent className="p-10 text-center">
                                            <CreditCard className="w-14 h-14 text-slate-200 mx-auto mb-4" />
                                            <p className="text-slate-500 mb-1 font-medium">Aucune carte externe</p>
                                            <p className="text-xs text-slate-400 mb-4">Ajoutez vos cartes de fidélité d'autres commerces ici</p>
                                            <Button
                                                onClick={() => setShowAddExternal(true)}
                                                className="bg-slate-800 hover:bg-slate-700 text-white gap-1.5"
                                                data-testid="add-external-card-empty-btn"
                                            >
                                                <Plus className="w-4 h-4" /> Ajouter une carte
                                            </Button>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <div className="space-y-2">
                                        {externalCards.map((ec) => (
                                            <motion.div
                                                key={ec.id}
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                className="group cursor-pointer"
                                                onClick={() => setPreviewExternal(ec)}
                                                data-testid={`external-card-${ec.id}`}
                                            >
                                                <div className="flex items-center gap-4 bg-white rounded-xl border border-slate-100 p-3 hover:border-slate-200 hover:shadow-sm transition-all">
                                                    <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                                        <CreditCard className="w-5 h-5 text-slate-400" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-slate-800 truncate">{ec.shop_name}</p>
                                                        <p className="text-xs text-slate-400">{ec.card_number ? `N° ${ec.card_number}` : 'Carte de fidélité'}</p>
                                                    </div>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleDeleteExternalCard(ec.id); }}
                                                        className="p-1.5 text-slate-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
                                                        data-testid={`delete-external-${ec.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </TabsContent>

                    {/* DISCOVER TAB */}
                    <TabsContent value="discover" className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="font-heading text-xl font-bold">Découvrir</h2>
                            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">{discoverCards.length + partnerCards.length} disponibles</Badge>
                        </div>

                        {discoverCards.length > 0 && (
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Rechercher un commerce ou programme..."
                                    value={discoverSearch}
                                    onChange={e => setDiscoverSearch(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-emerald-100 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
                                    data-testid="discover-search-input"
                                />
                                {discoverSearch && (
                                    <button onClick={() => setDiscoverSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="discover-search-clear">
                                        <span className="text-xs font-medium">✕</span>
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Partner Cards */}
                        {partnerCards.length > 0 && (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Handshake className="w-4 h-4 text-blue-500" />
                                    <p className="text-sm font-bold text-blue-700">Recommandés par vos commerçants</p>
                                </div>
                                {partnerCards.map((c, i) => (
                                    <Card key={c.id} className="border-blue-200 bg-blue-50/30 rounded-xl" data-testid={`partner-card-${c.id}`}>
                                        <CardContent className="p-3 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: c.card_color_from || '#3b82f6' }}>
                                                    <span className="text-white text-xs font-bold">{c.merchant_name?.charAt(0)}</span>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium">{c.name}</p>
                                                    <p className="text-xs text-slate-500">{c.merchant_name}</p>
                                                    {c.welcome_bonus > 0 && (
                                                        <p className="text-[10px] text-blue-600 font-medium">+{c.welcome_bonus} {c.type === 'stamp' ? 'tampons' : 'points'} de bienvenue</p>
                                                    )}
                                                </div>
                                            </div>
                                            <Button size="sm" onClick={() => handleJoin(c.id)} className="bg-blue-500 hover:bg-blue-600 text-white text-xs" data-testid={`join-partner-${c.id}`}>
                                                Rejoindre
                                            </Button>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}

                        {(() => {
                            const q = discoverSearch.toLowerCase().trim();
                            const filtered = q ? discoverCards.filter(c =>
                                (c.name || '').toLowerCase().includes(q) || (c.merchant_name || '').toLowerCase().includes(q)
                            ) : discoverCards;
                            return filtered.length === 0 && discoverCards.length > 0 ? (
                                <Card className="border-emerald-100/50 rounded-2xl">
                                    <CardContent className="p-8 text-center">
                                        <Search className="w-10 h-10 text-emerald-200 mx-auto mb-3" />
                                        <p className="text-sm text-slate-500">Aucun commerce trouve pour "{discoverSearch}"</p>
                                    </CardContent>
                                </Card>
                            ) : filtered.length === 0 ? (
                            <Card className="border-emerald-100/50 rounded-2xl">
                                <CardContent className="p-8 text-center">
                                    <Search className="w-12 h-12 text-emerald-200 mx-auto mb-4" />
                                    <p className="text-slate-500">Aucun nouveau programme disponible</p>
                                </CardContent>
                            </Card>
                        ) : (
                                <div className="space-y-3">
                                    {filtered.map((c, i) => (
                                    <motion.div
                                        key={c.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.05 }}
                                    >
                                        <Card className="border-emerald-100/50 rounded-xl hover:border-emerald-200 transition-all" data-testid={`discover-card-${c.id}`}>
                                            <CardContent className="p-4 flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                                                        style={{ background: `linear-gradient(135deg, ${getCardColor(i).from}, ${getCardColor(i).to})` }}
                                                    >
                                                        {c.type === 'stamp' ? <Stamp className="w-5 h-5 text-white" /> : <Star className="w-5 h-5 text-white" />}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold">{c.name}</p>
                                                        <p className="text-xs text-slate-500">{c.merchant_name}</p>
                                                        <p className="text-xs text-slate-400 mt-0.5">
                                                            {c.type === 'stamp' ? `${c.stamp_goal} tampons` : `${c.reward_points_cost} pts`} → {c.reward_description}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleJoin(c.id)}
                                                    className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs gap-1 active:scale-95 shrink-0"
                                                    data-testid={`join-card-${c.id}`}
                                                >
                                                    <Plus className="w-3.5 h-3.5" /> Rejoindre
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                ))}
                                </div>
                            );
                        })()}
                    </TabsContent>

                    {/* HISTORY TAB */}
                    <TabsContent value="history" className="space-y-4">
                        <h2 className="font-heading text-xl font-bold">Historique</h2>
                        {transactions.length === 0 ? (
                            <Card className="border-emerald-100/50 rounded-2xl">
                                <CardContent className="p-8 text-center">
                                    <Clock className="w-12 h-12 text-emerald-200 mx-auto mb-4" />
                                    <p className="text-slate-500">Aucune transaction</p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-2">
                                {transactions.map(tx => (
                                    <Card key={tx.id} className="border-emerald-100/50 rounded-xl" data-testid={`tx-${tx.id}`}>
                                        <CardContent className="p-4 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tx.type === 'redeem' ? 'bg-amber-100' : tx.type.includes('remove') ? 'bg-red-100' : 'bg-emerald-100'}`}>
                                                    {tx.type === 'redeem' ? <Gift className="w-4 h-4 text-amber-600" /> :
                                                     tx.type.includes('remove') ? <Star className="w-4 h-4 text-red-600" /> :
                                                     <Plus className="w-4 h-4 text-emerald-600" />}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium">{txTypeLabels[tx.type] || tx.type}</p>
                                                    <p className="text-xs text-slate-400">{new Date(tx.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                                                </div>
                                            </div>
                                            <span className={`font-heading font-bold text-sm ${tx.type.includes('remove') || tx.type === 'redeem' ? 'text-red-600' : 'text-emerald-600'}`}>
                                                {tx.type.includes('remove') || tx.type === 'redeem' ? '-' : '+'}{tx.amount}
                                            </span>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* PROMOS TAB */}
                    <TabsContent value="promos" className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="font-heading text-xl font-bold">Promotions</h2>
                            <Badge className="bg-red-100 text-red-600 border-0 text-xs">{clientPromos.length} offres</Badge>
                        </div>

                        {clientPromos.length === 0 ? (
                            <Card className="border-emerald-100/50 rounded-2xl">
                                <CardContent className="p-8 text-center">
                                    <Tag className="w-12 h-12 text-emerald-200 mx-auto mb-4" />
                                    <p className="text-slate-500">Aucune promotion en cours</p>
                                    <p className="text-xs text-slate-400 mt-1">Les offres de vos commerçants apparaîtront ici</p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {clientPromos.map((p, i) => {
                                    const daysLeft = Math.ceil((new Date(p.end_date) - new Date()) / 86400000);
                                    const discount = p.original_price > 0 && p.promo_price > 0 ? Math.round((1 - p.promo_price / p.original_price) * 100) : 0;
                                    return (
                                        <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                                            <Card className="border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow h-full" data-testid={`client-promo-${p.id}`}>
                                                {p.photo && (
                                                    <div className="w-full aspect-video relative overflow-hidden bg-slate-100">
                                                        <img src={p.photo} alt={p.title} className="w-full h-full object-cover" />
                                                        {discount > 0 && (
                                                            <div className="absolute top-3 right-3 bg-red-500 text-white text-sm font-bold px-2.5 py-1 rounded-lg shadow-lg">
                                                                -{discount}%
                                                            </div>
                                                        )}
                                                        {daysLeft > 0 && daysLeft <= 3 && (
                                                            <div className="absolute top-3 left-3 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-lg shadow-lg">
                                                                Plus que {daysLeft}j !
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                <CardContent className="p-4 space-y-2">
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <p className="font-bold text-base">{p.title}</p>
                                                            <p className="text-xs text-emerald-600 font-medium">{p.merchant_name}</p>
                                                        </div>
                                                        {!p.photo && discount > 0 && (
                                                            <Badge className="bg-red-500 text-white border-0 text-xs shrink-0">-{discount}%</Badge>
                                                        )}
                                                    </div>
                                                    {p.description && <p className="text-sm text-slate-500 line-clamp-2">{p.description}</p>}
                                                    <div className="flex items-center gap-3">
                                                        {p.original_price > 0 && (
                                                            <span className="line-through text-slate-400 text-sm">{p.original_price}€</span>
                                                        )}
                                                        {p.promo_price > 0 && (
                                                            <span className="font-bold text-red-600 text-lg">{p.promo_price}€</span>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap gap-2 pt-1">
                                                        {p.promo_code && (
                                                            <Badge className="bg-purple-100 text-purple-700 border-0 text-xs">
                                                                Code : {p.promo_code}
                                                            </Badge>
                                                        )}
                                                        {p.limited_quantity > 0 && (
                                                            <Badge className="bg-orange-100 text-orange-600 border-0 text-xs">
                                                                Limite : {p.limited_quantity} places
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    {p.conditions && (
                                                        <p className="text-[11px] text-slate-400 italic">Conditions : {p.conditions}</p>
                                                    )}
                                                    <div className="flex items-center gap-2 text-xs text-slate-400 pt-1">
                                                        <CalendarDays className="w-3.5 h-3.5 shrink-0" />
                                                        Du {new Date(p.start_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} au {new Date(p.end_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                        {daysLeft > 0 && daysLeft <= 7 && (
                                                            <Badge className="bg-orange-100 text-orange-600 border-0 text-[9px] ml-auto">Plus que {daysLeft}j</Badge>
                                                        )}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </main>

            {/* Expanded QR Dialog */}
            <Dialog open={!!selectedWallet} onOpenChange={() => setSelectedWallet(null)}>
                <DialogContent className="rounded-2xl p-0 overflow-hidden sm:max-w-[380px]" data-testid="qr-expanded-dialog">
                    {selectedWallet && selectedWallet.card && (
                        <div className="flex flex-col items-center p-5 sm:p-6 pt-8 max-h-[85vh] overflow-y-auto">
                            <DialogHeader className="text-center w-full mb-3">
                                <DialogTitle className="font-heading text-lg">{selectedWallet.card.name}</DialogTitle>
                                <p className="text-sm text-slate-500">{selectedWallet.card.merchant_name}</p>
                            </DialogHeader>

                            {(selectedWallet.merchant_active === false || selectedWallet.card.admin_disabled === true) && (
                                <div className="w-full mb-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-start gap-2" data-testid="suspended-dialog-banner">
                                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                    <div className="text-xs text-amber-900 leading-snug">
                                        <p className="font-semibold mb-0.5">Programme temporairement indisponible</p>
                                        <p className="text-amber-800/90">Vos points et avantages sont conservés et seront à nouveau utilisables dès la reprise du programme.</p>
                                    </div>
                                </div>
                            )}

                            <p className="text-xs text-slate-400 mb-2">Présentez ce code au commerçant</p>
                            <div className="bg-white p-4 rounded-2xl border-2 border-emerald-100 shadow-lg shadow-emerald-500/5 mb-3">
                                <QRCode value={selectedWallet.qr_code || 'N/A'} size={180} fgColor="#064e3b" />
                            </div>
                            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-base font-mono tracking-[0.2em] px-4 py-1.5 mb-3" data-testid="expanded-qr-code">
                                {selectedWallet.qr_code}
                            </Badge>

                            <div className="w-full mt-1 p-3 bg-gray-50 rounded-xl">
                                {selectedWallet.card.type === 'stamp' ? (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-600">Progression</span>
                                        <span className="font-heading font-bold text-emerald-700">{selectedWallet.stamps}/{selectedWallet.card.stamp_goal} tampons</span>
                                    </div>
                                ) : selectedWallet.card.type === 'tier' ? (
                                    <div>
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm font-bold text-slate-700">Mes paliers</span>
                                            <span className="text-lg font-heading font-bold text-emerald-700">{selectedWallet.points || 0} pts</span>
                                        </div>
                                        {(() => {
                                            const pts = selectedWallet.points || 0;
                                            const paliers = (selectedWallet.card.paliers_config || []).sort((a, b) => a.points - b.points);
                                            return (
                                                <div className="space-y-0">
                                                    {paliers.map((p, i) => {
                                                        const reached = pts >= p.points;
                                                        const isCurrent = !reached && (i === 0 || pts >= paliers[i - 1].points);
                                                        const prevPoints = i === 0 ? 0 : paliers[i - 1].points;
                                                        const progressInStep = isCurrent ? Math.min(((pts - prevPoints) / (p.points - prevPoints)) * 100, 100) : 0;
                                                        return (
                                                            <div key={i} className="flex gap-3" data-testid={`popup-palier-${i}`}>
                                                                {/* Timeline line */}
                                                                <div className="flex flex-col items-center">
                                                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 z-10 ${reached ? 'bg-emerald-500 text-white' : isCurrent ? 'border-2 border-emerald-400 bg-emerald-50' : 'border-2 border-slate-200 bg-white'}`}>
                                                                        {reached ? <Check className="w-3.5 h-3.5" /> : <span className="text-[9px] font-bold text-slate-400">{i + 1}</span>}
                                                                    </div>
                                                                    {i < paliers.length - 1 && (
                                                                        <div className={`w-0.5 h-8 ${reached ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                                                                    )}
                                                                </div>
                                                                {/* Content */}
                                                                <div className={`flex-1 pb-3 ${i < paliers.length - 1 ? '' : ''}`}>
                                                                    <div className="flex items-center justify-between">
                                                                        <span className={`text-sm font-semibold ${reached ? 'text-emerald-700' : isCurrent ? 'text-slate-800' : 'text-slate-400'}`}>{p.reward}</span>
                                                                        <span className={`text-xs font-mono ${reached ? 'text-emerald-500' : 'text-slate-400'}`}>{p.points} pts</span>
                                                                    </div>
                                                                    {reached && (
                                                                        <span className="text-[10px] text-emerald-600 font-medium">Atteint !</span>
                                                                    )}
                                                                    {isCurrent && (
                                                                        <div className="mt-1">
                                                                            <div className="bg-slate-200 rounded-full h-1.5 overflow-hidden">
                                                                                <div className="bg-emerald-500 rounded-full h-1.5 transition-all" style={{ width: `${progressInStep}%` }} />
                                                                            </div>
                                                                            <span className="text-[10px] text-slate-500 mt-0.5 block">Encore {p.points - pts} pts</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-600">Progression</span>
                                        <span className="font-heading font-bold text-emerald-700">{selectedWallet.points}/{selectedWallet.card.reward_points_cost} points</span>
                                    </div>
                                )}
                            </div>

                            {/* Shop Info */}
                            {(selectedWallet.card.shop_address || selectedWallet.card.shop_phone) && (
                                <div className="w-full mt-2 space-y-1.5">
                                    {selectedWallet.card.shop_address && (
                                        <a href={`https://maps.google.com/?q=${encodeURIComponent(selectedWallet.card.shop_address)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg text-xs text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors" data-testid="shop-address-link">
                                            <MapPin className="w-3.5 h-3.5 shrink-0" />
                                            <span className="truncate">{selectedWallet.card.shop_address}</span>
                                        </a>
                                    )}
                                    {selectedWallet.card.shop_phone && (
                                        <a href={`tel:${selectedWallet.card.shop_phone}`} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg text-xs text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors" data-testid="shop-phone-link">
                                            <Phone className="w-3.5 h-3.5 shrink-0" />
                                            <span>{selectedWallet.card.shop_phone}</span>
                                        </a>
                                    )}
                                </div>
                            )}

                            {/* Marketing Boost section */}
                            {(selectedWallet.card.referral_enabled || selectedWallet.card.social_instagram || selectedWallet.card.social_facebook || selectedWallet.card.social_tiktok || selectedWallet.card.social_snapchat || selectedWallet.card.review_google || selectedWallet.card.review_tripadvisor) && (
                                <div className="w-full mt-4 p-3 bg-gray-50 rounded-xl space-y-3" data-testid={`boost-${selectedWallet.id}`}>
                                    <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Gagnez plus de {selectedWallet.card.type === 'stamp' ? 'tampons' : 'points'}</p>

                                    {/* Referral */}
                                    {selectedWallet.card.referral_enabled && selectedWallet.referral_code && (
                                        <div className="flex items-center gap-2 p-2.5 bg-blue-50 rounded-lg" data-testid={`referral-section-${selectedWallet.id}`}>
                                            <Share2 className="w-4 h-4 text-blue-500 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-medium text-blue-700">Parrainez un ami</p>
                                                <p className="text-[10px] text-blue-500">Vous gagnez {selectedWallet.card.referral_sponsor_bonus} + votre ami gagne {selectedWallet.card.referral_godchild_bonus} {selectedWallet.card.type === 'stamp' ? 'tampons' : 'pts'}</p>
                                            </div>
                                            <button
                                                onClick={() => copyReferralCode(selectedWallet.referral_code)}
                                                className="flex items-center gap-1 px-2 py-1 bg-blue-500 text-white rounded-md text-[10px] font-bold hover:bg-blue-600 transition-colors shrink-0"
                                                data-testid={`copy-referral-${selectedWallet.id}`}
                                            >
                                                <Copy className="w-3 h-3" /> {selectedWallet.referral_code}
                                            </button>
                                        </div>
                                    )}

                                    {/* Social */}
                                    {(selectedWallet.card.social_instagram || selectedWallet.card.social_facebook || selectedWallet.card.social_tiktok || selectedWallet.card.social_snapchat) && (
                                        <div className="space-y-1.5">
                                            <p className="text-[10px] font-semibold text-purple-600 uppercase">Réseaux sociaux (+{selectedWallet.card.social_bonus} {selectedWallet.card.type === 'stamp' ? 'tamp.' : 'pts'}/réseau)</p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {[
                                                    { key: 'instagram', icon: '📸', label: 'Insta', link: selectedWallet.card.social_instagram },
                                                    { key: 'facebook', icon: '👤', label: 'Facebook', link: selectedWallet.card.social_facebook },
                                                    { key: 'tiktok', icon: '🎵', label: 'TikTok', link: selectedWallet.card.social_tiktok },
                                                    { key: 'snapchat', icon: '👻', label: 'Snap', link: selectedWallet.card.social_snapchat },
                                                ].filter(s => s.link).map(s => {
                                                    const claimed = (selectedWallet.social_claimed || []).includes(s.key);
                                                    return (
                                                        <button
                                                            key={s.key}
                                                            onClick={() => !claimed && claimSocialBonus(selectedWallet.id, s.key, s.link)}
                                                            disabled={claimed}
                                                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                                                                claimed ? 'bg-emerald-100 text-emerald-600 cursor-default' : 'bg-purple-100 text-purple-700 hover:bg-purple-200 active:scale-95'
                                                            }`}
                                                            data-testid={`social-${s.key}-${selectedWallet.id}`}
                                                        >
                                                            <span>{s.icon}</span> {s.label} {claimed && '✓'}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Reviews */}
                                    {(selectedWallet.card.review_google || selectedWallet.card.review_tripadvisor) && (
                                        <div className="space-y-1.5">
                                            <p className="text-[10px] font-semibold text-amber-600 uppercase">Avis en ligne (+{selectedWallet.card.review_bonus} {selectedWallet.card.type === 'stamp' ? 'tamp.' : 'pts'}/avis)</p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {[
                                                    { key: 'google', icon: '⭐', label: 'Google', link: selectedWallet.card.review_google },
                                                    { key: 'tripadvisor', icon: '🦉', label: 'TripAdvisor', link: selectedWallet.card.review_tripadvisor },
                                                ].filter(r => r.link).map(r => {
                                                    const claimed = (selectedWallet.review_claimed || []).includes(r.key);
                                                    return (
                                                        <button
                                                            key={r.key}
                                                            onClick={() => !claimed && claimReviewBonus(selectedWallet.id, r.key, r.link)}
                                                            disabled={claimed}
                                                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                                                                claimed ? 'bg-emerald-100 text-emerald-600 cursor-default' : 'bg-amber-100 text-amber-700 hover:bg-amber-200 active:scale-95'
                                                            }`}
                                                            data-testid={`review-${r.key}-${selectedWallet.id}`}
                                                        >
                                                            <span>{r.icon}</span> {r.label} {claimed && '✓'}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Profile Dialog */}
            <Dialog open={showProfile} onOpenChange={setShowProfile}>
                <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="profile-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Mon Profil</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <div>
                            <label className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                                <CalendarDays className="w-3.5 h-3.5" /> Date de naissance
                            </label>
                            <div className="grid grid-cols-3 gap-2" data-testid="profile-birthday-input">
                                <select
                                    value={profileForm.birthday ? parseInt(profileForm.birthday.split('-')[2]) : ''}
                                    onChange={e => {
                                        const parts = (profileForm.birthday || '2000-01-01').split('-');
                                        setProfileForm(p => ({ ...p, birthday: `${parts[0]}-${parts[1]}-${String(e.target.value).padStart(2,'0')}` }));
                                    }}
                                    className="px-2 py-2.5 bg-white border border-emerald-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300"
                                    data-testid="birthday-day"
                                >
                                    <option value="">Jour</option>
                                    {Array.from({length:31},(_,i)=>i+1).map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                                <select
                                    value={profileForm.birthday ? parseInt(profileForm.birthday.split('-')[1]) : ''}
                                    onChange={e => {
                                        const parts = (profileForm.birthday || '2000-01-01').split('-');
                                        setProfileForm(p => ({ ...p, birthday: `${parts[0]}-${String(e.target.value).padStart(2,'0')}-${parts[2]}` }));
                                    }}
                                    className="px-2 py-2.5 bg-white border border-emerald-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300"
                                    data-testid="birthday-month"
                                >
                                    <option value="">Mois</option>
                                    {['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'].map((m,i)=> <option key={i+1} value={i+1}>{m}</option>)}
                                </select>
                                <select
                                    value={profileForm.birthday ? parseInt(profileForm.birthday.split('-')[0]) : ''}
                                    onChange={e => {
                                        const parts = (profileForm.birthday || '2000-01-01').split('-');
                                        setProfileForm(p => ({ ...p, birthday: `${e.target.value}-${parts[1]}-${parts[2]}` }));
                                    }}
                                    className="px-2 py-2.5 bg-white border border-emerald-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300"
                                    data-testid="birthday-year"
                                >
                                    <option value="">Année</option>
                                    {Array.from({length:80},(_,i)=> new Date().getFullYear() - 10 - i).map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                            {!profileForm.birthday && <p className="text-[11px] text-orange-500 mt-1">Renseignez votre date de naissance pour recevoir des cadeaux !</p>}
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5" /> Numero de telephone
                            </label>
                            <input
                                type="tel"
                                placeholder="06 12 34 56 78"
                                value={profileForm.phone}
                                onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                                className="w-full px-3 py-2.5 bg-white border border-emerald-100 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
                                data-testid="profile-phone-input"
                            />
                        </div>
                        <label className="flex items-start gap-2.5 p-3 bg-slate-50 rounded-xl cursor-pointer" data-testid="rgpd-consent-label">
                            <input
                                type="checkbox"
                                checked={profileForm.rgpd_consent || false}
                                onChange={e => setProfileForm(p => ({ ...p, rgpd_consent: e.target.checked }))}
                                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-300 accent-emerald-500"
                                data-testid="rgpd-consent-checkbox"
                            />
                            <span className="text-[11px] text-slate-500 leading-relaxed">
                                J'accepte que mes données personnelles (date de naissance, téléphone) soient utilisées par les commerçants partenaires dans le cadre du programme de fidélité, conformément au <span className="text-emerald-600 font-medium">RGPD</span>. Vous pouvez retirer votre consentement à tout moment.
                            </span>
                        </label>
                        <Button onClick={saveProfile} disabled={!profileForm.rgpd_consent} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50 disabled:cursor-not-allowed" data-testid="profile-save-btn">
                            Enregistrer
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Leave Card Confirmation Dialog */}
            <Dialog open={!!leaveWallet} onOpenChange={() => setLeaveWallet(null)}>
                <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="leave-card-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Se désabonner</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-slate-600">
                            Voulez-vous vraiment vous désabonner de la carte <strong>{leaveWallet?.card?.name || 'cette carte'}</strong> ?
                        </p>
                        <p className="text-xs text-red-500 font-medium">
                            Vos points et tampons seront définitivement perdus. Vous pourrez vous réabonner mais vos points repartiront à zéro.
                        </p>
                        <div className="flex gap-3 justify-end pt-2">
                            <Button variant="outline" size="sm" onClick={() => setLeaveWallet(null)} data-testid="cancel-leave-btn">
                                Annuler
                            </Button>
                            <Button variant="destructive" size="sm" onClick={handleLeaveCard} data-testid="confirm-leave-btn">
                                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                Se désabonner
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add External Card Dialog */}
            <Dialog open={showAddExternal} onOpenChange={(open) => { setShowAddExternal(open); if (!open) setExternalForm({ shop_name: '', card_number: '' }); }}>
                <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="add-external-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Ajouter une carte</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Nom de la carte</label>
                            <input
                                type="text"
                                placeholder="Ex: E.Leclerc - Carte de fidélité"
                                value={externalForm.shop_name}
                                onChange={(e) => setExternalForm(prev => ({ ...prev, shop_name: e.target.value }))}
                                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300"
                                data-testid="external-shop-name-input"
                            />
                        </div>

                        {/* Card number input with scanner */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-slate-700">Numéro de la carte</label>
                                <button
                                    type="button"
                                    onClick={() => { setShowAddExternal(false); setShowBarcodeScanner(true); }}
                                    className="flex items-center gap-1 px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-medium transition-colors"
                                    data-testid="open-barcode-scanner-btn"
                                >
                                    <ScanBarcode className="w-3.5 h-3.5" /> Scanner
                                </button>
                            </div>
                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder="Ex: 2959002704295"
                                value={externalForm.card_number}
                                onChange={(e) => setExternalForm(prev => ({ ...prev, card_number: e.target.value }))}
                                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-mono placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 tracking-wider"
                                data-testid="external-card-number-input"
                            />
                            <p className="text-[10px] text-slate-400">Entrez les chiffres ou scannez le code-barres de votre carte</p>
                        </div>

                        <Button
                            onClick={handleAddExternalCard}
                            disabled={externalLoading || !externalForm.shop_name.trim() || !externalForm.card_number.trim()}
                            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
                            data-testid="external-save-btn"
                        >
                            {externalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer la carte'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Preview External Card Dialog - Samsung Wallet style */}
            <Dialog open={!!previewExternal} onOpenChange={() => { setPreviewExternal(null); setBarcodeZoom(false); }}>
                <DialogContent className="rounded-2xl p-0 overflow-hidden sm:max-w-[420px]" data-testid="preview-external-dialog">
                    {previewExternal && (
                        <div className="bg-white">
                            {/* Card Name */}
                            <div className="px-6 pt-5 pb-2">
                                <h3 className="text-lg font-bold text-slate-900">{previewExternal.shop_name}</h3>
                            </div>

                            {/* Barcode Section */}
                            {previewExternal.card_number && (
                                <div
                                    className="px-6 pb-4 cursor-pointer"
                                    onClick={() => setBarcodeZoom(!barcodeZoom)}
                                    data-testid="barcode-section"
                                >
                                    <div className={`bg-white rounded-xl border border-slate-100 p-4 flex flex-col items-center transition-all ${barcodeZoom ? 'py-8' : ''}`}>
                                        <Barcode
                                            value={previewExternal.card_number}
                                            width={barcodeZoom ? 2.5 : 1.8}
                                            height={barcodeZoom ? 80 : 50}
                                            fontSize={0}
                                            margin={0}
                                            background="transparent"
                                        />
                                        <p className="text-sm font-mono text-slate-600 mt-2 tracking-widest">{previewExternal.card_number}</p>
                                    </div>
                                    <p className="text-[11px] text-slate-400 text-center mt-2">Appuyez sur le code-barres pour faire un zoom avant</p>
                                </div>
                            )}

                            {/* No barcode - show placeholder */}
                            {!previewExternal.card_number && (
                                <div className="px-6 pb-4">
                                    <div className="bg-slate-50 rounded-xl p-8 flex flex-col items-center">
                                        <CreditCard className="w-12 h-12 text-slate-300 mb-2" />
                                        <p className="text-sm text-slate-400">Carte de fidélité</p>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="px-6 pb-5 flex items-center justify-end">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteExternalCard(previewExternal.id)}
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50 gap-1"
                                    data-testid="preview-delete-external-btn"
                                >
                                    <Trash2 className="w-4 h-4" /> Supprimer
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Barcode Camera Scanner */}
            {showBarcodeScanner && (
                <BarcodeCameraScanner
                    onScan={(code) => {
                        setExternalForm(prev => ({ ...prev, card_number: code }));
                        setShowBarcodeScanner(false);
                        setShowAddExternal(true);
                        toast.success(`Code-barres détecté : ${code}`);
                    }}
                    onClose={() => { setShowBarcodeScanner(false); setShowAddExternal(true); }}
                />
            )}

            <Footer role="client" />
        </div>
    );
}
